import { z } from 'zod';

/**
 * Two-level digest over a matrix's context files (2026-08-26 design doc,
 * D9-D19).
 *
 * Level 1 — one digest per file, derived from that file's extracted text
 * alone. Level 2 — one synthesis per matrix, derived from the *digests* and
 * never from the raw text, which is what lets it reconcile overlap and
 * surface contradictions between documents.
 *
 * Generation and regeneration consume digests, never the raw extracted text:
 * roughly 7.5k characters instead of up to 200k (D10).
 */

/**
 * Where a digest's content came from (D19), mirroring
 * `cellVersionOriginEnum`. A hand-edited digest is a first-class version, not
 * an annotation on a generated one — the user who knows exactly what the
 * digest should say should not have to coax it out of a prompt.
 */
export const digestOriginSchema = z.enum(['generated', 'manual']);
export type DigestOrigin = z.infer<typeof digestOriginSchema>;

/** Target length the digest prompts aim for. Advisory (stated in the prompt), with a hard cap below. */
export const DIGEST_TARGET_CHARS = 2_000;

/**
 * Hard ceiling per digest and per synthesis.
 *
 * A prompt-stated target is a request, not a guarantee: a model that ignores
 * it would otherwise put the whole point of the digest layer at risk by
 * pushing a 50k-character "digest" into every regeneration prompt. Truncation
 * at this ceiling is the backstop.
 */
export const DIGEST_MAX_CHARS = 8_000;

/** Longest comment a generation accepts, in characters. */
export const DIGEST_COMMENT_MAX_CHARS = 2_000;

/** A comment steering one regeneration. Persisted on the version it produced, never as a standing instruction (D18). */
export const digestCommentSchema = z.string().trim().min(1).max(DIGEST_COMMENT_MAX_CHARS);

export const digestVersionSchema = z.object({
  id: z.string().uuid(),
  contextFileId: z.string().uuid(),
  content: z.string(),
  /** The comment that steered this regeneration, if any. Applies to this version only. */
  comment: z.string().nullable(),
  /** The model that produced it — null for a hand-written version. */
  model: z.string().nullable(),
  origin: digestOriginSchema,
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid().nullable(),
});
export type DigestVersion = z.infer<typeof digestVersionSchema>;

export const synthesisVersionSchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  content: z.string(),
  comment: z.string().nullable(),
  model: z.string().nullable(),
  origin: digestOriginSchema,
  /**
   * The exact set of digest versions this synthesis was built from (D13).
   *
   * Freshness is decided by comparing this set with the current set of
   * per-file digest versions, rather than by comparing timestamps: set
   * equality has no clock races, and it detects a *removed* file, which a
   * "newest digest is older than the synthesis" check would silently miss.
   */
  sourceDigestVersionIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid().nullable(),
});
export type SynthesisVersion = z.infer<typeof synthesisVersionSchema>;

/** One file's digest status, for the review surface in the wizard and the matrix drawer (D23). */
export const fileDigestStateSchema = z.object({
  contextFileId: z.string().uuid(),
  filename: z.string(),
  /** Null when this file has never been digested — the phase-skip logic digests exactly these (D16). */
  currentVersion: digestVersionSchema.nullable(),
  versionCount: z.number().int().nonnegative(),
});
export type FileDigestState = z.infer<typeof fileDigestStateSchema>;

/**
 * Everything the UI needs to decide what to show and what to offer, and what
 * the engine needs to decide which phases to skip.
 */
export const matrixDigestStateSchema = z.object({
  files: z.array(fileDigestStateSchema),
  synthesis: synthesisVersionSchema.nullable(),
  /**
   * True when every file has a digest, so the synthesis phase has complete
   * input. False means a `full` generation would still have digesting to do.
   */
  allFilesDigested: z.boolean(),
  /**
   * True when `synthesis.sourceDigestVersionIds` equals the current set of
   * per-file digest version ids (D13). False — including when there is no
   * synthesis at all — means the synthesis phase must run.
   *
   * Always true for a single file, where the synthesis step is skipped
   * entirely and that file's digest *is* the context (D11).
   */
  synthesisCurrent: z.boolean(),
  /** True when no phase would run: generation can go straight to cells (D16). */
  ready: z.boolean(),
  /**
   * Files whose current digest was produced under a framing the matrix no
   * longer has — its `createdAt` precedes `matrices.framingUpdatedAt` (issue
   * #1).
   *
   * Reported, never acted on: these ids are deliberately absent from the work
   * a generation would do. A digest is extracted *for* a given sujet/contexte,
   * so a framing change genuinely dates it — but re-deriving one costs a model
   * call per document and would overwrite a digest someone wrote by hand.
   * Which of them is worth redoing is the user's call, not the engine's.
   */
  staleFileIds: z.array(z.string().uuid()),
  /**
   * True when the synthesis rests on at least one stale digest, or was itself
   * written under the previous framing. Distinct from `synthesisCurrent`,
   * which is about the digests having *moved*, not about the framing.
   */
  synthesisStale: z.boolean(),
});
export type MatrixDigestState = z.infer<typeof matrixDigestStateSchema>;

/** `POST /context-files/:id/digest-versions` and the synthesis equivalent — regenerate, optionally steered. */
export const regenerateDigestInputSchema = z.object({
  comment: digestCommentSchema.optional(),
});
export type RegenerateDigestInput = z.infer<typeof regenerateDigestInputSchema>;

/**
 * `POST /matrices/:id/digest-jobs` — the bulk run, optionally steered by one comment (issue #20).
 *
 * The comment goes to every digest the job produces and to the synthesis that follows, and is
 * recorded on those versions only — never on the job row, and never as a standing instruction.
 */
export const startDigestJobInputSchema = z.object({
  comment: digestCommentSchema.optional(),
});
export type StartDigestJobInput = z.infer<typeof startDigestJobInputSchema>;

/** `PUT /context-files/:id/digest` and the synthesis equivalent — replace the text by hand (D19). */
export const editDigestInputSchema = z.object({
  content: z.string().trim().min(1).max(DIGEST_MAX_CHARS),
});
export type EditDigestInput = z.infer<typeof editDigestInputSchema>;
